import { Link } from "react-router-dom";
import { observer } from "mobx-react";
import { Button, Text } from "@mantine/core";
import { BsFillPlayFill, BsPauseFill } from "react-icons/bs";
import type { ReactNode } from "react";
import { EpisodeMenu } from "./EpisodeMenu";
import { EpisodeProgress } from "./EpisodeProgress";
import styles from "./styles/EpisodeRow.module.css";
import { usePlayEpisode } from "@/hooks/usePlayEpisode";
import { useStores } from "@/hooks/useStores";
import {
    createDurationMin,
    filterText,
    filterTitle,
    getPodcastImage,
    ImageSizes,
    timeAgo,
    truncate,
} from "@/core/helpers";

interface EpisodeRowProps {
    episode: Episode;
    podcast: Podcast;
    /** Show the podcast cover and name (lists that mix podcasts) */
    showPodcast?: boolean;
    /** Show a short description under the title */
    showSnippet?: boolean;
    /** Extra controls rendered next to the play button */
    extra?: ReactNode;
    /** Show the "more actions" menu in the top-right corner */
    withMenu?: boolean;
    /** A short line under the title block, e.g. time remaining */
    note?: ReactNode;
}

/**
 * One episode in a list: cover, title, meta line and a round play button.
 * Shared by the category feed and the podcast episode list.
 */
export const EpisodeRow = observer(
    ({
        episode,
        podcast,
        showPodcast = false,
        showSnippet = false,
        extra,
        withMenu = false,
        note,
    }: EpisodeRowProps) => {
        const { listeningStore } = useStores();
        const { play, pause, isPlaying } = usePlayEpisode(episode, podcast);
        const played = listeningStore.isPlayed(episode.id);
        const isVideo = episode.sound.endsWith(".mp4");
        const duration = episode.length ? createDurationMin(episode.length) : 0;

        return (
            <li className={`${styles.row} ${withMenu ? styles.withMenu : ""}`}>
                {withMenu && <EpisodeMenu className={styles.menu} episode={episode} podcast={podcast} />}
                {showPodcast && (
                    <div className={styles.cover}>
                        <img
                            alt=""
                            height={ImageSizes.Thumb}
                            loading="lazy"
                            src={getPodcastImage(podcast, ImageSizes.Small)}
                            width={ImageSizes.Thumb}
                        />
                    </div>
                )}
                <div className={styles.text}>
                    <Link className={styles.title} to={`/podd/${podcast.slug}/${episode.slug}`}>
                        <span>{filterTitle(episode.title)}</span>
                    </Link>
                    {showSnippet && episode.snippet && (
                        <p className={styles.snippet}>{truncate(filterText(episode.snippet), 174)}</p>
                    )}
                    <div className={styles.meta}>
                        {showPodcast && (
                            <Link className={styles.show} to={`/podd/${podcast.slug}`}>
                                {podcast.name}
                            </Link>
                        )}
                        <Text c="dimmed" component="span" size="sm">
                            <time dateTime={episode.published}>{timeAgo(episode.published)}</time>
                            {duration > 0 && ` · ${duration} min`}
                            {played && " · Spelad"}
                        </Text>
                    </div>
                    <EpisodeProgress episodeId={episode.id} showPlayed={false} />
                    {note}
                </div>
                <div className={styles.actions}>
                    {extra}
                    {isVideo ? (
                        <Button
                            component={Link}
                            size="compact-md"
                            to={`/podd/${podcast.slug}/${episode.slug}`}
                            variant="light">
                            Titta
                        </Button>
                    ) : (
                        <button
                            aria-label={`${isPlaying ? "Pausa" : "Spela"} ${episode.title}`}
                            className={styles.play}
                            onClick={isPlaying ? pause : play}
                            type="button">
                            {isPlaying ? <BsPauseFill size={22} /> : <BsFillPlayFill size={22} />}
                        </button>
                    )}
                </div>
            </li>
        );
    }
);
